import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, Clock, TrendingUp } from 'lucide-react';
interface UrgencyBannerProps {
  variant?: 'light' | 'dark';
}
const COUNTDOWN_KEY = 'mda_offer_deadline_v1';
const DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const getDeadline = (): number => {
  if (typeof window === 'undefined') return Date.now() + DURATION_MS;
  try {
    const stored = localStorage.getItem(COUNTDOWN_KEY);
    if (stored) {
      const ts = parseInt(stored, 10);
      if (!isNaN(ts) && ts > Date.now()) return ts;
    }
    const next = Date.now() + DURATION_MS;
    localStorage.setItem(COUNTDOWN_KEY, String(next));
    return next;
  } catch {
    return Date.now() + DURATION_MS;
  }
};
const formatUnit = (n: number) => String(Math.max(0, n)).padStart(2, '0');
export const UrgencyBanner: React.FC<UrgencyBannerProps> = ({
  variant = 'light'
}) => {
  const [deadline, setDeadline] = useState<number>(() => typeof window !== 'undefined' ? getDeadline() : Date.now() + DURATION_MS);
  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    setDeadline(getDeadline());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = Math.max(0, deadline - now);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const isDark = variant === 'dark';
  const TimeUnit = ({
    value,
    label
  }: {
    value: number;
    label: string;
  }) => <div className="flex flex-col items-center" data-id="element-301">
      <motion.div key={value} initial={{
      opacity: 0.4,
      y: -4
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.2
    }} className={`tabular-nums font-bold font-serif text-2xl md:text-3xl leading-none ${isDark ? 'text-cream' : 'text-white'}`} data-id="element-302">
        {formatUnit(value)}
      </motion.div>
      <span className={`text-[9px] uppercase tracking-widest mt-1 ${isDark ? 'text-cream/60' : 'text-white/80'}`} data-id="element-303">
        {label}
      </span>
    </div>;
  return <motion.div initial={{
    opacity: 0,
    y: -10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="max-w-3xl mx-auto mb-8" data-id="element-304">
      <div className="relative rounded-sm overflow-hidden shadow-xl bg-gradient-to-r from-red-700 via-red-600 to-red-700 border border-red-800/50" data-id="element-305">
        {/* Subtle animated shine */}
        <motion.div aria-hidden animate={{
        x: ['-100%', '200%']
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut',
        repeatDelay: 2
      }} className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent pointer-events-none" data-id="element-306" />

        <div className="relative p-4 md:p-5" data-id="element-307">
          {/* Header */}
          <div className="flex items-center justify-center gap-2 mb-3" data-id="element-308">
            <motion.div animate={{
            scale: [1, 1.15, 1]
          }} transition={{
            duration: 1.4,
            repeat: Infinity
          }} data-id="element-309">
              <Flame className="w-5 h-5 text-yellow-300" data-id="element-310" />
            </motion.div>
            <p className="text-white font-bold tracking-widest text-xs md:text-sm uppercase" data-id="element-311">
              ¡Oferta termina pronto!
            </p>
            <motion.div animate={{
            scale: [1, 1.15, 1]
          }} transition={{
            duration: 1.4,
            repeat: Infinity,
            delay: 0.7
          }} data-id="element-312">
              <Flame className="w-5 h-5 text-yellow-300" data-id="element-313" />
            </motion.div>
          </div>

          {/* Main row: countdown + warning */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6" data-id="element-314">
            {/* Countdown */}
            <div className="flex items-center gap-2 bg-black/25 px-4 py-2.5 rounded-sm border border-white/10" data-id="element-315">
              <Clock className="w-4 h-4 text-yellow-300" data-id="element-316" />
              <div className="flex items-center gap-2" data-id="element-317">
                <TimeUnit value={hours} label="HRS" data-id="element-318" />
                <span className="text-white text-xl font-bold opacity-60" data-id="element-319">
                  :
                </span>
                <TimeUnit value={minutes} label="MIN" data-id="element-320" />
                <span className="text-white text-xl font-bold opacity-60" data-id="element-321">
                  :
                </span>
                <TimeUnit value={seconds} label="SEG" data-id="element-322" />
              </div>
            </div>

            {/* Warning text */}
            <div className="text-center md:text-left flex-1" data-id="element-323">
              <p className="text-white text-sm md:text-base font-bold leading-snug flex items-center justify-center md:justify-start gap-1.5" data-id="element-324">
                <TrendingUp className="w-4 h-4 text-yellow-300 flex-shrink-0" data-id="element-325" />
                <span data-id="element-326">
                  Cuando termine, el precio sube{' '}
                  <span className="text-yellow-300" data-id="element-327">+ $10.000</span>
                </span>
              </p>
              <p className="text-white/80 text-xs mt-0.5" data-id="element-328">
                Y no volverá a bajar. Asegurá tu lugar ahora.
              </p>
            </div>
          </div>

          {/* Progress bar — cupos */}
          <div className="mt-4" data-id="element-329">
            <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/85 mb-1.5 font-semibold" data-id="element-330">
              <span data-id="element-331">Cupos con descuento</span>
              <span data-id="element-332">68% tomados</span>
            </div>
            <div className="h-2 bg-black/30 rounded-full overflow-hidden border border-white/10" data-id="element-333">
              <motion.div initial={{
              width: 0
            }} whileInView={{
              width: '68%'
            }} viewport={{
              once: true
            }} transition={{
              duration: 1.2,
              delay: 0.3,
              ease: 'easeOut'
            }} className="h-full bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 relative" data-id="element-334">
                <motion.div aria-hidden animate={{
                x: ['-100%', '200%']
              }} transition={{
                duration: 1.8,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/40 to-transparent" data-id="element-335" />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
};